import React from 'react'
import Animated from 'react-native-reanimated'
import { ErrorBoundary } from 'react-error-boundary'

import { View, XStack, YStack, useMedia } from 'tamagui'

import { Text } from '@newComponents/Text'
import { Card } from '@newComponents/Card'
import { GuildCard } from '@newComponents/Guild/GuildCard'
import { EventItem } from '@newComponents/Event/EventItem'

// Legacy component - no Tamagui replacement available yet
import { PresentationItem } from '@components/Presentation/PresentationItem'

import { HomeScreenGradient } from '../HomeScreenGradient'

import {
  HeroMainText,
  HeroText,
  HeroCopyText,
  MarkerExtrasText,
  PURPLE_BOX_SHADOW,
  FILLER_GRADIENT_BACKGROUND,
} from './styles'
import { selectMediaValue, fontLineHeight } from './utils'
import { CallToActionButton } from './CallToActionButton'
import { HeroAnimation } from './useHeroAnimation'
import { UnauthenticatedHomeScreen_query$data } from './__generated__/UnauthenticatedHomeScreen_query.graphql'

export function DesktopHeroSection({
  data,
  animation,
}: {
  data: UnauthenticatedHomeScreen_query$data
  animation: HeroAnimation
}) {
  const media = useMedia()
  const {
    gradientContainerRef,
    guildCardRef,
    gradient,
    setGradient,
    bigCardStyle,
    guildCardStyle,
    eventCardStyle,
  } = animation

  const desktopHeroLineHeight = selectMediaValue(media, [
    fontLineHeight(7),
    fontLineHeight(11),
    fontLineHeight(14),
    fontLineHeight(15),
  ])

  return (
    <View position='relative' width='100%'>
      <View
        ref={gradientContainerRef}
        borderRadius='$6'
        overflow='hidden'
        width='100%'
        style={{
          backgroundImage: `url(${FILLER_GRADIENT_BACKGROUND})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          boxShadow: PURPLE_BOX_SHADOW,
        }}
        onLayout={() => {
          if (gradient) {
            gradient.resize()
          }
        }}
      >
        <ErrorBoundary
          fallback={
            <View height={selectMediaValue(media, [590, 650, 950])} />
          }
        >
          <HomeScreenGradient
            getGradient={(gradient) => setGradient(gradient)}
            height={590}
            $gtXs={{
              height: 650,
            }}
            $gtSm={{
              height: 950,
            }}
          />
        </ErrorBoundary>
      </View>

      <View
        position='absolute'
        top={0}
        width='100%'
        paddingTop={120}
        paddingHorizontal={120}
        style={{ mixBlendMode: 'overlay' }}
      >
        <HeroMainText
          size='$8'
          $gtXs={{ size: '$12' }}
          $gtSm={{ size: '$15' }}
          $gtMd={{ size: '$16' }}
          textAlign='center'
        >
          <span className='notranslate'>ELEVATE</span>
        </HeroMainText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        top={selectMediaValue(media, [0, 0, 210, 240])}
        left={selectMediaValue(media, [0, 0, 80, 130])}
        style={{
          mixBlendMode: 'overlay',
          transform: 'rotate(20deg)',
        }}
      >
        <MarkerExtrasText
          size='$7'
          $gtXs={{ size: '$8' }}
          $gtSm={{ size: '$9' }}
          $gtMd={{ size: '$10' }}
          textAlign='center'
        >
          I
        </MarkerExtrasText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        top={selectMediaValue(media, [0, 0, 225, 255])}
        left={selectMediaValue(media, [0, 0, 85, 110])}
        style={{ mixBlendMode: 'overlay' }}
      >
        <MarkerExtrasText
          size='$8'
          $gtXs={{ size: '$12' }}
          $gtSm={{ size: '$16' }}
          $gtMd={{ size: '$17' }}
          textAlign='center'
        >
          v
        </MarkerExtrasText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        style={{
          top: `calc(120px + ${desktopHeroLineHeight}px - 12px)`,
        }}
      >
        <YStack alignItems='center' gap='$10'>
          <View paddingHorizontal={120}>
            <YStack alignItems='center' gap='$10'>
              <HeroText
                size='$8'
                $gtXs={{ size: '$10' }}
                $gtSm={{ size: '$11' }}
                $gtMd={{ size: '$12' }}
                textAlign='center'
                className='notranslate'
              >
                Your Community
              </HeroText>

              <YStack gap='$1' alignItems='center'>
                <HeroCopyText size='$7' textAlign='center'>
                  Guild is the platform for communities.
                </HeroCopyText>

                <HeroCopyText size='$7' textAlign='center'>
                  No matter the shape.
                </HeroCopyText>
              </YStack>

              <CallToActionButton />
            </YStack>
          </View>

          <View
            ref={guildCardRef}
            maxWidth='100%'
            paddingHorizontal={60}
          >
            <Animated.View style={bigCardStyle}>
              <XStack gap='$8'>
                {data.nodeBySlugId &&
                  'pastEvents' in data.nodeBySlugId && (
                    <YStack
                      flex={2}
                      $gtMd={{ flex: 1 }}
                      style={{ flexBasis: '0%' }}
                    >
                      <Animated.View style={guildCardStyle}>
                        <GuildCard
                          guild={data.nodeBySlugId}
                          limitDescriptionLines={10}
                          showMoreDialog
                          expand
                        />
                      </Animated.View>
                    </YStack>
                  )}

                <YStack
                  flex={3}
                  $gtMd={{ flex: 2 }}
                  gap='$10'
                  style={{ flexBasis: '0%' }}
                >
                  <XStack gap='$4' alignItems='center'>
                    <Text size='$4' color='$grey7' fontWeight='$5'>
                      Events
                    </Text>

                    <View
                      flex={1}
                      height={1}
                      backgroundColor='$grey4'
                    />
                  </XStack>

                  <Animated.View style={eventCardStyle}>
                    <EventItem
                      padding='$6'
                      paddingBottom='$5'
                      borderRadius='$8'
                      // @ts-expect-error web-only CSS
                      boxShadow={PURPLE_BOX_SHADOW}
                      event={
                        data.nodeBySlugId?.upcomingEvents?.edges[0]
                          ?.node
                          ? data.nodeBySlugId.upcomingEvents.edges[0]
                              .node
                          : data.nodeBySlugId?.pastEvents?.edges[0]
                              ?.node!
                      }
                      forceVertical={media.gtSm && !media.gtMd}
                      limitPresentations={1}
                      query={data}
                    />
                  </Animated.View>

                  <XStack gap='$4' alignItems='center'>
                    <Text size='$4' color='$grey7' fontWeight='$5'>
                      Presentations
                    </Text>

                    <View
                      flex={1}
                      height={1}
                      backgroundColor='$grey4'
                    />
                  </XStack>

                  <Animated.View
                    style={
                      {
                        transform: [
                          { perspective: 500 },
                          { translateZ: 0 },
                        ],
                      } as unknown as import('react-native').ViewStyle
                    }
                  >
                    <Card
                      padding='$6'
                      borderRadius='$8'
                      // @ts-expect-error web-only CSS
                      boxShadow={PURPLE_BOX_SHADOW}
                    >
                      {(data.nodeBySlugId?.upcomingPresentations
                        ?.edges[0]?.node ||
                        data.nodeBySlugId?.pastPresentations?.edges[0]
                          ?.node) && (
                        <PresentationItem
                          presentation={
                            (data.nodeBySlugId?.upcomingPresentations
                              ?.edges[0]?.node ??
                              data.nodeBySlugId?.pastPresentations
                                ?.edges[0]?.node)!
                          }
                          numberOfLines={4}
                          openAsModal={true}
                        />
                      )}
                    </Card>
                  </Animated.View>
                </YStack>
              </XStack>
            </Animated.View>
          </View>
        </YStack>
      </View>
    </View>
  )
}
